import { PencilLine } from 'lucide-react';
import type { ReactNode } from 'react';
import type { RequestEditMark, RequestEditMarks, RequestPart } from '@nest-recruit/shared';
import { Popover, usePopover } from '../../../components/Popover';
import { formatDateTime } from '../../../lib/format';
import styles from './EditedMark.module.css';

/**
 * ADR 0075 (workflow review B2, Q4 a): the small "Edited by HR" mark beside a part someone other than the requester changed.
 * Clicking it opens, next to the mark, what the requester first wrote, who changed it and when. Used on the request form, the
 * request page and, carried with the job, in the job builder.
 */

const POPOVER_WIDTH = 360;

export function EditedMark({ mark }: { readonly mark: RequestEditMark | undefined }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  if (!mark) return null;
  const times = mark.editCount === 1 ? '' : ` · changed ${mark.editCount} times`;
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.mark}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={toggle}
      >
        <PencilLine size={12} aria-hidden />
        Edited by HR
      </button>
      <Popover anchor={anchor} onClose={close} width={POPOVER_WIDTH} label={`${mark.partLabel}: what changed`}>
        <div className={styles.panel}>
          <p className={styles.who}>
            {mark.partLabel}: changed by <b>{mark.editedByName ?? 'a former colleague'}</b>, {formatDateTime(mark.editedAt)}
            {times}
          </p>
          <span className={styles.label}>The requester first wrote</span>
          <p className={styles.text}>{mark.originalText}</p>
          <span className={styles.label}>It now says</span>
          <p className={styles.text}>{mark.currentText}</p>
        </div>
      </Popover>
    </>
  );
}

/** The marks for some parts in one row, for a section that holds several (the job's Basics, the brief). */
export function EditedMarksRow({ marks, parts }: { readonly marks: RequestEditMarks; readonly parts: readonly RequestPart[] }): ReactNode {
  const shown = parts.flatMap((part) => {
    const mark = marks[part];
    return mark ? [mark] : [];
  });
  if (shown.length === 0) return null;
  return (
    <div className={styles.row} role="note">
      <span className={styles.rowLead}>HR changed what the requester wrote:</span>
      {shown.map((mark) => (
        <span key={mark.part} className={styles.rowItem}>
          {mark.partLabel}
          <EditedMark mark={mark} />
        </span>
      ))}
    </div>
  );
}
